import { ReactNode, useState, useEffect } from 'react';
import { router, usePage } from '@inertiajs/react';
import Footer from '../components/Footer';

const cursive = {
  fontFamily: 'cursive',
  fontSize: '1.2rem',
};

export const Navbar = () => {
  const [theme, setTheme] = useState('light');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Get theme from localStorage or system preference
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    const resolvedTheme = savedTheme || (prefersDark ? 'dark' : 'light');
    setTheme(resolvedTheme);
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleMenuToggle = () => setMenuOpen(open => !open);
  const closeMenu = () => setMenuOpen(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.get('/search', { q: searchQuery.trim() });
    }
  };

  const navGroups = [
    {
      label: 'Explore',
      items: [
        { label: 'Parks', href: '/parks' },
        { label: 'Resorts', href: '/resorts' },
        { label: 'Hotels', href: '/hotels' },
        { label: 'Restaurants', href: '/restaurants' },
        { label: 'Sites and Tools', href: '/sites' },
      ],
    },
    {
      label: 'Media',
      items: [
        { label: 'Podcasts', href: '/podcasts' },
        { label: 'Latest Episodes', href: '/episodes' },
        { label: 'Games', href: '/games/theme-park-guess' },
      ],
    },
    {
      label: 'Shop',
      items: [
        { label: 'Products', href: '/products' },
        { label: 'Stores', href: '/stores' },
      ],
    },
  ];

  return (
    <nav className="flex items-center justify-between px-4 py-2 bg-base-100 border-b border-base-200 relative">
      {/* Left: Logo and Hamburger */}
      <div className="flex items-center gap-2">
        <a
          href="/"
          className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center"
        >
          <span
            className="font-script text-2xl font-bold text-white antialiased"
            style={cursive}
          >
            RCG
          </span>
        </a>
        {/* Hamburger for mobile */}
        <button
          className="ml-2 flex md:hidden items-center justify-center p-2 rounded focus:outline-none focus:ring-2 focus:ring-primary"
          onClick={handleMenuToggle}
          aria-label="Toggle menu"
        >
          <svg
            className="h-6 w-6 text-neutral"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {menuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
        {/* Mega menu (hidden on mobile) */}
        <div className="hidden md:block ml-6">
          <div className="dropdown dropdown-hover dropdown-bottom">
            <div
              tabIndex={0}
              role="button"
              className="font-semibold text-lg text-neutral hover:text-primary transition-colors px-3 py-2 pb-3 -mb-2 rounded-lg hover:bg-base-200"
            >
              Explore
            </div>
            <div
              tabIndex={0}
              className="dropdown-content bg-base-100 rounded-xl z-[60] w-[36rem] p-4 shadow-xl border border-base-200 mt-0"
            >
              <div className="grid grid-cols-3 gap-4">
                {navGroups.map(group => (
                  <div key={group.label}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-base-content/60 mb-2">
                      {group.label}
                    </h3>
                    <ul className="menu p-0 gap-1">
                      {group.items.map(item => (
                        <li key={item.href}>
                          <a href={item.href} className="font-medium">
                            {item.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Search and Theme Toggle */}
      <div className="flex items-center gap-2">
        {/* Search form - hidden on mobile, shown on md+ screens */}
        <form
          onSubmit={handleSearch}
          className="hidden md:flex items-center bg-base-100 rounded-lg overflow-hidden h-10 border border-base-300"
        >
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="px-3 py-1.5 outline-none text-neutral bg-base-100 text-base h-full border-none focus:ring-0"
            style={{ minWidth: 160 }}
          />
          <button
            type="submit"
            aria-label="Search"
            className="bg-blue-600 hover:bg-blue-700 hover:scale-105 transition-all duration-200 px-3 h-full flex items-center justify-center rounded-none"
            style={{
              borderTopRightRadius: '0.5rem',
              borderBottomRightRadius: '0.5rem',
            }}
          >
            {/* Search Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-4-4m0 0A7 7 0 104 4a7 7 0 0013 13z"
              />
            </svg>
          </button>
        </form>

        {/* Mobile search button - only visible on mobile */}
        <button
          onClick={() => router.get('/search')}
          className="md:hidden p-2 rounded-lg bg-base-100 hover:bg-base-200 transition-colors flex items-center justify-center h-10 w-10 border border-base-300"
          aria-label="Search"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-neutral"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-4-4m0 0A7 7 0 104 4a7 7 0 0013 13z"
            />
          </svg>
        </button>

        {/* Theme toggle button - always visible */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-base-100 hover:bg-base-200 transition-colors flex items-center justify-center h-10 w-10 border border-base-300"
          aria-label="Toggle theme"
        >
          {/* Sun Icon for light mode, Moon Icon for dark mode */}
          {theme === 'light' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-neutral"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-neutral"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364-6.364l-1.414 1.414M6.05 17.95l-1.414 1.414m12.728 0l-1.414-1.414M6.05 6.05L4.636 4.636M12 8a4 4 0 100 8 4 4 0 000-8z"
              />
            </svg>
          )}
        </button>
      </div>
      {/* Mobile menu (absolute, only on mobile) */}
      {menuOpen && (
        <ul className="absolute top-full left-0 w-full bg-base-100 border-b border-base-200 flex flex-col gap-2 py-4 px-6 md:hidden z-50 shadow-lg animate-fade-in">
          <li>
            <a
              className="font-semibold text-lg text-neutral hover:text-primary transition-colors"
              href="/search"
              onClick={closeMenu}
            >
              Search
            </a>
          </li>
          {navGroups.map(group => (
            <li key={group.label}>
              <details className="group">
                <summary className="list-none cursor-pointer font-semibold text-lg text-neutral hover:text-primary transition-colors py-1 flex items-center justify-between">
                  {group.label}
                  <span className="text-base-content/60 group-open:rotate-180 transition-transform">
                    ▾
                  </span>
                </summary>
                <ul className="mt-1 ml-3 flex flex-col gap-1">
                  {group.items.map(item => (
                    <li key={item.href}>
                      <a
                        className="text-base text-neutral/90 hover:text-primary transition-colors"
                        href={item.href}
                        onClick={closeMenu}
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
};

export default function Layout({ children }: { children: ReactNode }) {
  const { props } = usePage();
  const flash = (
    props as {
      flash?: {
        notice?: string | null;
        alert?: string | null;
      };
    }
  ).flash;
  const flashMessage = flash?.alert || flash?.notice;
  const flashType = flash?.alert ? 'alert' : flash?.notice ? 'notice' : null;

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar />
      {flashMessage && (
        <div
          role="alert"
          className={`mx-auto mt-4 w-full max-w-4xl border px-4 py-3 text-sm ${
            flashType === 'alert'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-green-200 bg-green-50 text-green-800'
          }`}
        >
          {flashMessage}
        </div>
      )}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
